import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { SnackBarFacade, displayMessage } from '@ra-state';
import { DataService } from '@services/data.service';
import { Observable, combineLatest, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CommandRequestBuilderService } from 'src/app/services/command-request-builder.service';
import { TrialEvents } from 'src/app/services/command-request.service';
import { CampaignDetails, IDomainMessage, IDomainUpdateResponse, TrialBundle } from '../common-admin.model';
import {
  cancelReservation,
  clearTrialGridRefresh,
  createCampaign,
  endCampaign,
  getCampaignDetails,
  getTrialBundles,
  pauseCampaign,
  refreshTrialGrid,
  reserveTrial,
  resumeCampaign,
  sendRedemptionLinks,
  setCampaignDetails,
  setTrialBundles,
  updateCampaign,
} from './trials.action';

@Injectable({
  providedIn: 'root',
})
export class TrialsEffects {
  private readonly actions$ = inject(Actions);
  private readonly dataService = inject(DataService);
  private readonly commandRequestBuilderService = inject(CommandRequestBuilderService);
  private readonly router = inject(Router);
  private readonly snackBarFacade = inject(SnackBarFacade);

  reserveTrial$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(reserveTrial),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new(
            `/TrialCampaigns/${payload.payload.campaignId}/reserve`,
            'POST',
            // eslint-disable-next-line wrap-iife
            (function (countOfEventsToWaitFor: number, eventType: string): (domainMessage) => boolean {
              let countOfEventsReceived: number = 0;
              return (domainMessage): boolean => {
                if (domainMessage.type === eventType) {
                  countOfEventsReceived += 1;
                }
                return countOfEventsToWaitFor === countOfEventsReceived;
              };
            })(payload.payload.customerInfo.count, TrialEvents.TrialReserved),
          )
          .withBody(payload.payload.customerInfo)
          .withWaitOn201Created()
          .withErrorHandler((error: unknown) =>
            of({
              error: error,
              errorContext: {
                message:
                  'Not enough reservations are available to complete your request. Please refresh and try again.',
                type: 'Error',
              },
            } as IDomainUpdateResponse),
          );
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload.payload)]);
      }),
      switchMap(([res, payload]) => {
        this.router.navigate(['trials/campaign', payload.campaignId], {
          queryParams: { selectedTabIndex: 1 },
        });
        if (res.errorContext) {
          return [
            displayMessage({
              payload: {
                message: res.errorContext.message,
                type: res.errorContext.type,
              },
            }),
          ];
        }
        return [
          displayMessage({
            payload: {
              message: 'Trial was successfully reserved.',
              type: 'Success',
            },
          }),
        ];
      }),
    );
  });

  cancelReservation$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(cancelReservation),
      switchMap((payload) => {
        const cancelReservationRequests = payload.trialReservations.map((reservation) => {
          const commandRequest = this.commandRequestBuilderService
            .new(
              `/TrialCampaigns/${reservation.campaignId}/reservations/${reservation.reservationId}/cancel`,
              'PUT',
              TrialEvents.TrialUnreserved,
            )
            .withWaitOn202Accepted()
            .withErrorHandler((error: unknown) =>
              of({
                error: error,
                errorContext: {
                  message: 'Some trials could not be cancelled. Refresh and try again.',
                  type: 'Warning',
                },
              } as IDomainUpdateResponse),
            );

          return this.dataService.commandRequest$(commandRequest);
        });

        return forkJoin(cancelReservationRequests).pipe(
          map((res) => {
            const customErrorContext = res.find((domainRes) => domainRes.errorContext);
            return {
              errorContext: customErrorContext?.errorContext,
              campaignId: payload.trialReservations[0].campaignId,
            };
          }),
        );
      }),
      switchMap((res) => {
        const actions: Action[] = [];
        actions.push(
          displayMessage({
            payload: {
              message: res?.errorContext?.message || 'Trial reservation successfully cancelled.',
              type: res?.errorContext?.type || 'Success',
            },
          }),
          getCampaignDetails({ payload: res.campaignId }),
          refreshTrialGrid(),
          clearTrialGridRefresh(),
        );
        return actions;
      }),
    );
  });

  createCampaign$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(createCampaign),
      switchMap((payload) => {
        const commandRequest = this.commandRequestBuilderService
          .new('/TrialCampaigns/bundle', 'POST', TrialEvents.CampaignDescriptionChanged)
          .withBody(payload.payload)
          .withWaitOn201Created();
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload)]).pipe(
          catchError((error: unknown) => {
            this.router.navigate(['trials'], {
              queryParams: { campaignType: payload.payload.campaignType },
            });
            return throwError(() => error);
          }),
        );
      }),
      switchMap(([_response, payload]) => {
        this.router.navigate(['trials'], {
          queryParams: { campaignType: payload.payload.campaignType },
        });
        return [
          displayMessage({
            payload: {
              message: 'New trial campaign created successfully.',
              type: 'Success',
            },
          }),
        ];
      }),
    );
  });

  getCampaignDetails$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getCampaignDetails),
      switchMap((payload) => {
        return this.dataService.getCampaignDetails$(payload.payload);
      }),
      switchMap((campaignDetails: CampaignDetails) => {
        return [setCampaignDetails({ payload: campaignDetails })];
      }),
    );
  });

  pauseCampaign$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(pauseCampaign),
      switchMap((payload) => {
        const requestUrl = `/TrialCampaigns/${payload.campaignId}/pause`;
        const commandRequest = this.commandRequestBuilderService
          .new(requestUrl, 'PUT', TrialEvents.CampaignPaused)
          .withWaitOn202Accepted();
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload.campaignId)]);
      }),
      switchMap(([res, campaignId]) => {
        let msg = 'The trial campaign was paused successfully.';
        let type = 'Success';
        if (res.response?.status === 200) {
          msg = 'The trial campaign was already paused.';
          type = 'Warning';
        }
        return [
          displayMessage({
            payload: {
              message: msg,
              type: type,
            },
          }),
          getCampaignDetails({ payload: campaignId }),
        ];
      }),
    );
  });

  resumeCampaign$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(resumeCampaign),
      switchMap((payload) => {
        const requestUrl = `/TrialCampaigns/${payload.campaignId}/resume`;
        const commandRequest = this.commandRequestBuilderService
          .new(requestUrl, 'PUT', TrialEvents.CampaignResumed)
          .withWaitOn202Accepted();
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload.campaignId)]);
      }),
      switchMap(([res, campaignId]) => {
        let msg = 'The trial campaign was resumed successfully.';
        let type = 'Success';
        if (res.response?.status === 200) {
          msg = 'The trial campaign was already resumed.';
          type = 'Warning';
        }
        return [
          displayMessage({
            payload: {
              message: msg,
              type: type,
            },
          }),
          getCampaignDetails({ payload: campaignId }),
        ];
      }),
    );
  });

  endCampaign$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(endCampaign),
      switchMap((payload) => {
        const requestUrl = `/TrialCampaigns/${payload.campaignId}/end`;
        const commandRequest = this.commandRequestBuilderService
          .new(requestUrl, 'PUT', TrialEvents.CampaignEnded)
          .withWaitOn202Accepted();
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload.campaignId)]);
      }),
      switchMap(([res, campaignId]) => {
        let msg = 'The trial campaign was ended successfully.';
        let type = 'Success';
        if (res.response?.status === 200) {
          msg = 'The trial campaign was already ended.';
          type = 'Warning';
        }
        return [
          displayMessage({
            payload: {
              message: msg,
              type: type,
            },
          }),
          getCampaignDetails({ payload: campaignId }),
        ];
      }),
    );
  });

  getTrialBundles$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(getTrialBundles),
      switchMap(() => {
        return this.dataService.getTrialBundles$();
      }),
      switchMap((trialBundles: TrialBundle[]) => {
        return [setTrialBundles({ payload: trialBundles })];
      }),
    );
  });

  updateCampaign$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(updateCampaign),
      switchMap((payload) => {
        const requestUrl = `/TrialCampaigns/${payload.payload.id}/update`;
        const commandRequest = this.commandRequestBuilderService
          .new(requestUrl, 'PUT', (domainMessage: IDomainMessage) => {
            return (
              domainMessage.type === TrialEvents.CampaignNameChanged ||
              domainMessage.type === TrialEvents.CampaignDescriptionChanged
            );
          })
          .withBody(payload.payload.update)
          .withWaitOn202Accepted()
          .withErrorHandler((error: unknown) =>
            of({
              error: error,
              errorContext: payload.payload.id,
            } as IDomainUpdateResponse),
          );
        return combineLatest([this.dataService.commandRequest$(commandRequest), of(payload.payload.id)]);
      }),
      switchMap(([res, campaignId]) => {
        if (!res.error) {
          return [
            displayMessage({
              payload: {
                message: 'Trial Campaign updated successfully.',
                type: 'Success',
              },
            }),
            getCampaignDetails({ payload: campaignId }),
          ];
        }
        const actions: Action[] = [];
        const message = res.error.error.errorCode
          .split(/(?=[A-Z])/)
          .join(' ')
          .toLowerCase();
        actions.push(
          displayMessage({
            payload: {
              message: `Unable to update the trial campaign: The ${message}`,
              type: 'Error',
            },
          }),
          getCampaignDetails({ payload: campaignId }),
        );
        return actions;
      }),
    );
  });

  sendRedemptionLinks$ = createEffect((): Observable<any> => {
    return this.actions$.pipe(
      ofType(sendRedemptionLinks),
      switchMap((payload) => {
        return this.dataService.sendRedemptionLinks$(payload.payload.campaignId, payload.payload.reservationIds).pipe(
          switchMap((response) => {
            const msg = `Successfully sent ${response.emailsSent} trial reservation links via email!`;
            return [
              displayMessage({
                payload: {
                  message: msg,
                  type: 'Success',
                },
              }),
            ];
          }),
          catchError((_error: unknown) => {
            return [
              displayMessage({
                payload: {
                  message: 'Something went wrong. Please refresh the page and try again.',
                  type: 'Error',
                },
              }),
            ];
          }),
        );
      }),
    );
  });
}
